import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon, PersonAddAlt1Outlined as JoinIcon } from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import PostCard from '../../components/community/PostCard';
import PostComposer from '../../components/community/PostComposer';
import { Loading, PostCardSkeleton, StackSkeleton } from '../../components/layout/Skeletons';
import { useFeed } from '../../hooks/community/useFeed';
import { useAuth } from '../../context/AuthContext';
import { POST_TYPES } from '../../constants/community';

/**
 * The neighbourhood feed: what people are asking, giving away and announcing.
 *
 * The five types are a row of chips rather than a select. There are only
 * five, they are the main thing anybody filters on, and a select hides its
 * options behind a tap — which is exactly the interaction the audience is
 * least sure of. The neighbourhood stays a select: that list grows.
 */
function Feed() {
  const { isAuthenticated } = useAuth();
  const {
    filters,
    posts,
    meta,
    city,
    neighborhoods,
    loading,
    error,
    setFilter,
    resetFilters,
    reload,
  } = useFeed();

  const hasFilters = Boolean(filters.type || filters.neighborhood || filters.q);

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          reserveLede
          title="Le fil du quartier"
          lede={
            city
              ? `Ce qui se dit entre voisins à ${city.name}. Tout le monde peut lire ; il faut un compte pour écrire.`
              : 'Ce qui se dit entre voisins près de chez vous.'
          }
          action={
            /* Reading the feed is what convinces somebody to join it, so the
               way in sits where the composer will be once they have: the lede
               says an account is needed to write, and this is the account. */
            isAuthenticated ? (
              <PostComposer neighborhoods={neighborhoods} onPublished={reload} />
            ) : (
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                size="large"
                startIcon={<JoinIcon />}
              >
                Créer un compte
              </Button>
            )
          }
        />

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, mb: 3.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <Typography variant="subtitle2" component="p" sx={{ mb: 1.25 }}>
              Afficher
            </Typography>
            <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="Tout"
                onClick={() => setFilter('type', '')}
                aria-pressed={!filters.type}
                sx={
                  filters.type
                    ? { bgcolor: 'background.paper', border: '1.5px solid', borderColor: 'surface.borderStrong' }
                    : { bgcolor: 'secondary.main', color: 'secondary.contrastText', fontWeight: 700 }
                }
              />
              {POST_TYPES.map((option) => {
                const chosen = filters.type === option.value;

                return (
                  <Chip
                    key={option.value}
                    label={option.label}
                    onClick={() => setFilter('type', chosen ? '' : option.value)}
                    aria-pressed={chosen}
                    sx={
                      chosen
                        ? { bgcolor: 'secondary.main', color: 'secondary.contrastText', fontWeight: 700 }
                        : { bgcolor: 'background.paper', border: '1.5px solid', borderColor: 'surface.borderStrong' }
                    }
                  />
                );
              })}
            </Stack>
          </Box>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Rechercher"
              id="feed-search"
              value={filters.q}
              onChange={(e) => setFilter('q', e.target.value)}
              placeholder="Un mot du titre ou du message"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              label="Quartier"
              id="feed-neighborhood"
              // Shown only once it is among the options: the list arrives after the
              // filter, which the URL already carries, and MUI warns on a value it
              // cannot show. The filter itself stays applied meanwhile.
              value={neighborhoods.some((n) => n.slug === filters.neighborhood) ? filters.neighborhood : ''}
              onChange={(e) => setFilter('neighborhood', e.target.value)}
              sx={{ minWidth: { md: 260 } }}
              disabled={0 === neighborhoods.length}
            >
              <MenuItem value="">Tout le quartier</MenuItem>
              {neighborhoods.map((neighborhood) => (
                <MenuItem key={neighborhood.slug} value={neighborhood.slug}>
                  {neighborhood.name}
                </MenuItem>
              ))}
            </TextField>

            {hasFilters && (
              <Button onClick={resetFilters} variant="outlined" sx={{ whiteSpace: 'nowrap', height: 56 }}>
                Effacer
              </Button>
            )}
          </Stack>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Loading label="Chargement du fil du quartier">
            <StackSkeleton count={4}>{PostCardSkeleton}</StackSkeleton>
          </Loading>
        ) : (
          <Stack spacing={2.25}>
            {0 === posts.length && (
              <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
                <Typography variant="h4" component="p" sx={{ mb: 1 }}>
                  {hasFilters ? 'Rien ne correspond à cette recherche.' : 'Le fil est encore vide.'}
                </Typography>
                <Typography variant="body1">
                  {hasFilters
                    ? 'Essayez un autre type ou un autre quartier.'
                    : isAuthenticated
                      ? 'Soyez le premier à écrire quelque chose.'
                      : 'Créez un compte pour écrire le premier message.'}
                </Typography>
              </Paper>
            )}

            {posts.map((post) => (
              <PostCard key={post.uuid} post={post} onModerated={reload} />
            ))}
          </Stack>
        )}

        {meta && meta.total_pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <Pagination
              count={meta.total_pages}
              page={meta.current_page}
              onChange={(event, page) => setFilter('page', String(page))}
            />
          </Box>
        )}
      </Box>
    </PageLayout>
  );
}

export default Feed;
